/**
 * manual-quote-print-page.tsx — Bản in A4 của báo giá thủ công. Route `/platform/manual-quotes/:id/print`
 * (NGOÀI PlatformLayout để in trọn trang — tự kiểm tra đăng nhập platform).
 *
 * Bố cục theo docs/mau-bieu/don-dat-hang-thu-cong.html: đầu trang (logo + bên bán, "BÁO GIÁ / ĐƠN ĐẶT HÀNG", số, ngày),
 * khách hàng, nội dung đặt hàng (dòng theo bậc giá), dịch vụ khác, tổng (VAT), bằng chữ, thanh toán (ngân hàng +
 * mã VietQR + nội dung chuyển khoản = số báo giá), điều khoản, chữ ký. Số liệu lấy từ ảnh chụp đã lưu (không tính lại).
 * "In / Lưu PDF" = window.print(); @page A4 như mẫu.
 */
import { useEffect } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { usePlatformAuthStore } from '@/stores/platform-auth-store'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { useManualQuote } from '@/hooks/use-manual-quotes'
import { QUOTA_KEYS, QUOTA_LABELS } from '../pricing-editor'
import {
  endDate, fmt, moneyWords, ORDER_TYPE_LABELS, STATUS_LABELS, termLines, transferNote, viDate, vietQrUrl, type ManualQuote,
} from './manual-quote-helpers'

const PRINT_CSS = `
.mq-desk { background: #e9ecf3; min-height: 100vh; color: #161b2e; font: 13px/1.5 "Be Vietnam Pro", "Segoe UI", Roboto, Arial, sans-serif; font-variant-numeric: tabular-nums; }
.mq-paper { max-width: 210mm; min-height: 297mm; margin: 16px auto; background: #fff; padding: 14mm 14mm 12mm; box-shadow: 0 2px 16px rgba(22,27,46,.12); }
.mq-head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; padding-bottom: 10px; border-bottom: 2px solid #0045ff; }
.mq-seller { display: flex; gap: 12px; min-width: 0; flex: 1; }
.mq-logo { width: 64px; height: 64px; flex: none; object-fit: contain; }
.mq-seller-info { display: grid; gap: 1px; font-size: 11.5px; color: #5d6478; }
.mq-seller-info .name { font-size: 14px; font-weight: 700; color: #161b2e; text-transform: uppercase; }
.mq-title { text-align: right; flex: none; }
.mq-title h1 { margin: 0; font-size: 20px; letter-spacing: .04em; color: #0045ff; font-weight: 800; }
.mq-title .sub { font-size: 11px; color: #5d6478; text-transform: uppercase; letter-spacing: .08em; }
.mq-title .meta { margin-top: 6px; display: grid; gap: 2px; }
.mq h2 { margin: 16px 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: #0045ff; font-weight: 700; display: flex; gap: 8px; align-items: center; }
.mq h2::after { content: ""; flex: 1; border-top: 1px solid #d9dde8; }
.mq-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; }
.mq-fields .full { grid-column: 1 / -1; }
.mq-fld { display: flex; gap: 6px; align-items: baseline; }
.mq-fld > span { flex: none; color: #5d6478; }
.mq-fld > b { font-weight: 500; border-bottom: 1px dotted #9aa1b5; flex: 1; min-height: 1.5em; }
.mq-items { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12.5px; }
.mq-items th { background: #eef3ff; font-weight: 600; font-size: 11.5px; padding: 6px; border: 1px solid #d9dde8; text-align: left; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.mq-items td { padding: 5px 6px; border: 1px solid #d9dde8; vertical-align: top; }
.mq-items .r { text-align: right; white-space: nowrap; }
.mq-items .c { text-align: center; }
.mq-items .desc { color: #5d6478; font-size: 11.5px; }
.mq-items tr.sum td { font-weight: 600; background: #fafbfd; }
.mq-totals { display: grid; grid-template-columns: 1fr minmax(260px, 50%); gap: 16px; margin-top: 10px; }
.mq-totals table { width: 100%; border-collapse: collapse; }
.mq-totals td { padding: 3px 0; }
.mq-totals td.r { text-align: right; white-space: nowrap; padding-left: 12px; }
.mq-totals tr.grand td { border-top: 2px solid #161b2e; padding-top: 6px; font-size: 15px; font-weight: 800; }
.mq-totals tr.grand td.r { color: #0045ff; }
.mq-totals .minus { color: #b42318; }
.mq-words { margin-top: 6px; font-style: italic; }
.mq-pay { display: grid; grid-template-columns: 1fr auto; gap: 16px; align-items: start; }
.mq-qr { width: 136px; text-align: center; font-size: 10.5px; color: #5d6478; }
.mq-qr img { width: 136px; display: block; border: 1px solid #d9dde8; border-radius: 6px; }
.mq-note { margin-top: 6px; padding: 6px 10px; background: #eef3ff; border-radius: 6px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.mq-terms { font-size: 11.5px; color: #333a4f; }
.mq-terms ol { margin: 0; padding-left: 18px; }
.mq-sign { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 18px; text-align: center; }
.mq-sign .who { font-weight: 700; text-transform: uppercase; font-size: 12px; }
.mq-sign .how { font-size: 11px; color: #5d6478; font-style: italic; }
.mq-sign .space { height: 76px; }
.mq-foot { margin-top: 14px; padding-top: 6px; border-top: 1px solid #d9dde8; font-size: 10.5px; color: #5d6478; text-align: center; }
.mq-toolbar { position: sticky; top: 0; z-index: 10; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 10px 16px; background: #161b2e; color: #fff; }
.mq-toolbar strong { margin-right: auto; font-size: 14px; }
@media (max-width: 720px) {
  .mq-paper { padding: 16px; min-height: 0; margin: 0; }
  .mq-head, .mq-pay { flex-direction: column; grid-template-columns: 1fr; }
  .mq-title { text-align: left; }
  .mq-fields, .mq-totals, .mq-sign { grid-template-columns: 1fr; }
}
@page { size: A4; margin: 10mm 12mm; }
@media print {
  .mq-desk { background: #fff; font-size: 12px; }
  .mq-toolbar { display: none !important; }
  .mq-paper { box-shadow: none; max-width: none; min-height: 0; padding: 0; margin: 0; }
  tr, .mq-sign, .mq-pay { break-inside: avoid; }
}
`

export function ManualQuotePrintPage() {
  const { admin, loading, loadMe } = usePlatformAuthStore()
  const location = useLocation()
  useEffect(() => {
    if (!admin) loadMe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (loading) return <Loading label="Đang tải..." className="h-screen" />
  if (!admin) return <Navigate to="/platform/login" state={{ from: location }} replace />
  return <PrintView />
}

function PrintView() {
  const { id } = useParams()
  const { data: q, isError } = useManualQuote(id)
  useEffect(() => {
    if (!q) return
    const prev = document.title
    document.title = `Bao-gia-${q.number}`
    return () => { document.title = prev }
  }, [q])
  if (isError) return <ErrorState message="Không tải được báo giá" />
  if (!q) return <Loading label="Đang tải..." className="h-screen" />
  return (
    <div className="mq-desk">
      <style>{PRINT_CSS}</style>
      <div className="mq-toolbar">
        <strong>Báo giá {q.number} — {STATUS_LABELS[q.displayStatus]}</strong>
        <Button size="sm" variant="secondary" asChild><Link to={`/platform/manual-quotes/${q.id}`}><ArrowLeft className="h-4 w-4" /> Về báo giá</Link></Button>
        <Button size="sm" onClick={() => window.print()}><Printer className="h-4 w-4" /> In / Lưu PDF</Button>
      </div>
      <QuoteDocument q={q} />
    </div>
  )
}

export function QuoteDocument({ q }: { q: ManualQuote }) {
  const s = q.seller
  const c = q.customer
  const b = q.breakdown
  const note = transferNote(q.number)
  const qr = vietQrUrl(s, q.total, note)
  const created = q.createdAt.slice(0, 10)
  const start = typeof q.input.startDate === 'string' ? q.input.startDate : ''
  const terms = termLines(s.terms)
  const minus = (label: string, v: number) => <tr><td>{label}</td><td className="r minus">−{fmt(v)} ₫</td></tr>
  return (
    <article className="mq mq-paper">
      <header className="mq-head">
        <div className="mq-seller">
          <img className="mq-logo" src={s.logoUrl || '/assets/logo-mark.png'} alt="" />
          <div className="mq-seller-info">
            <span className="name">{s.companyName}</span>
            {s.address && <span>Địa chỉ: {s.address}</span>}
            {s.taxCode && <span>MST: {s.taxCode}</span>}
            <span>{[s.phone && `ĐT: ${s.phone}`, s.email && `Email: ${s.email}`].filter(Boolean).join(' · ')}</span>
            {s.website && <span>{s.website}</span>}
          </div>
        </div>
        <div className="mq-title">
          <div className="sub">Phần mềm quản lý chat đa kênh</div>
          <h1>BÁO GIÁ / ĐƠN ĐẶT HÀNG</h1>
          <div className="meta">
            <span>Số: <b>{q.number}</b></span>
            <span>Ngày: {viDate(created)}</span>
          </div>
        </div>
      </header>

      <h2>1. Thông tin khách hàng (bên mua)</h2>
      <div className="mq-fields">
        <div className="mq-fld full"><span>Tên đơn vị / cá nhân:</span><b style={{ fontWeight: 600 }}>{c.name}</b></div>
        <div className="mq-fld"><span>Mã số thuế:</span><b>{c.taxCode}</b></div>
        <div className="mq-fld"><span>Người đại diện:</span><b>{c.representative}</b></div>
        <div className="mq-fld full"><span>Địa chỉ:</span><b>{c.address}</b></div>
        <div className="mq-fld"><span>Người liên hệ:</span><b>{c.contact}</b></div>
        <div className="mq-fld"><span>Điện thoại:</span><b>{c.phone}</b></div>
        <div className="mq-fld"><span>Email:</span><b>{c.email}</b></div>
        <div className="mq-fld"><span>Tài khoản eCDP:</span><b>{q.org?.name ?? (q.orgId ? '' : 'Tạo mới khi thanh toán')}</b></div>
      </div>

      <h2>2. Nội dung đặt hàng</h2>
      <div className="mq-fields">
        <div className="mq-fld"><span>Loại đơn:</span><b>{ORDER_TYPE_LABELS[q.input.orderType] ?? ''}</b></div>
        <div className="mq-fld"><span>Gói dịch vụ:</span><b>{b.plan.name}</b></div>
        <div className="mq-fld"><span>Thời hạn:</span><b>{b.days} ngày{b.extraDays > 0 ? ` (+${b.extraDays} ngày quy đổi)` : ''}</b></div>
        <div className="mq-fld">
          <span>{b.addon ? 'Đến hết kỳ:' : 'Từ ngày (dự kiến):'}</span>
          <b>{b.addon ? viDate(b.addon.periodEnd) : start ? `${viDate(start)} – ${viDate(endDate(start, b.totalDays))}` : ''}</b>
        </div>
        <div className="mq-fld full"><span>Số lượng:</span><b>{QUOTA_KEYS.map((k) => `${(b.addon ? b.addon.from[k] + b.addon.delta[k] : (q.input.quantities as Record<string, number> | undefined)?.[k]) ?? ''} ${QUOTA_LABELS[k].toLowerCase()}`).join(' · ')}</b></div>
      </div>
      <table className="mq-items">
        <thead>
          <tr><th className="c" style={{ width: 32 }}>STT</th><th>Hạng mục</th><th className="c" style={{ width: 56 }}>SL</th><th className="r" style={{ width: 120 }}>Đơn giá / tháng (₫)</th><th className="r" style={{ width: 120 }}>Thành tiền / tháng (₫)</th></tr>
        </thead>
        <tbody>
          {b.items.map((it, i) => (
            <tr key={i}><td className="c">{i + 1}</td><td>{it.label} <span className="desc">({it.description})</span></td><td className="c">{it.quantity}</td><td className="r">{fmt(it.unitPrice)}</td><td className="r">{fmt(it.amount)}</td></tr>
          ))}
          <tr className="sum"><td /><td colSpan={3}>Cộng giá / tháng</td><td className="r">{fmt(b.perMonth)}</td></tr>
        </tbody>
      </table>
      {b.extras.lines.length > 0 && (
        <table className="mq-items">
          <thead>
            <tr><th className="c" style={{ width: 32 }}>STT</th><th>Dịch vụ khác (trả một lần)</th><th className="c" style={{ width: 56 }}>SL</th><th className="r" style={{ width: 120 }}>Đơn giá (₫)</th><th className="r" style={{ width: 120 }}>Thành tiền (₫)</th></tr>
          </thead>
          <tbody>
            {b.extras.lines.map((e, i) => (
              <tr key={i}><td className="c">{i + 1}</td><td>{e.name}</td><td className="c">{e.quantity}</td><td className="r">{fmt(e.unitPrice)}</td><td className="r">{fmt(e.amount)}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mq-totals">
        <div>
          {b.promo && <div className="mq-fld"><span>Mã ưu đãi:</span><b>{b.promo.code}</b></div>}
          {q.note && <div className="mq-fld"><span>Ghi chú:</span><b>{q.note}</b></div>}
        </div>
        <table>
          <tbody>
            <tr><td>Cước {b.days} ngày ({fmt(b.perMonth)} × {b.days}/30)</td><td className="r">{fmt(b.subtotal)} ₫</td></tr>
            {b.discount > 0 && minus(`Giảm giá thời hạn ${b.discountPercent}%`, b.discount)}
            {b.promo && minus(`Mã ưu đãi ${b.promo.code}`, b.promo.amount)}
            {b.credit && minus(`Quy đổi ${b.credit.remainingDays} ngày còn lại gói ${b.credit.fromPlanName}`, b.credit.applied)}
            {b.manualDiscount && minus('Giảm thêm theo thoả thuận', b.manualDiscount.amount)}
            {b.extras.preTax > 0 && <tr><td>Dịch vụ khác</td><td className="r">{fmt(b.extras.preTax)} ₫</td></tr>}
            <tr><td>Cộng trước thuế</td><td className="r">{fmt(b.preTaxTotal)} ₫</td></tr>
            <tr><td>Thuế GTGT {b.vatPercent}%</td><td className="r">{fmt(b.vatTotal)} ₫</td></tr>
            <tr className="grand"><td>TỔNG THANH TOÁN</td><td className="r">{fmt(q.total)} ₫</td></tr>
          </tbody>
        </table>
      </div>
      <div className="mq-words">Bằng chữ: <b style={{ fontStyle: 'normal' }}>{moneyWords(q.total)}.</b></div>

      <h2>3. Thanh toán</h2>
      <div className="mq-pay">
        <div>
          <div className="mq-fields">
            <div className="mq-fld"><span>Hình thức:</span><b>{s.paymentMethod}</b></div>
            <div className="mq-fld"><span>Hạn thanh toán:</span><b>{viDate(q.dueDate)}</b></div>
            <div className="mq-fld"><span>Ngân hàng:</span><b>{s.bankName}</b></div>
            <div className="mq-fld"><span>Số tài khoản:</span><b style={{ fontWeight: 600 }}>{s.bankAccount}</b></div>
            <div className="mq-fld full"><span>Chủ tài khoản:</span><b style={{ textTransform: 'uppercase' }}>{s.bankHolder}</b></div>
          </div>
          <div className="mq-note">Nội dung chuyển khoản: <b>{note}</b></div>
        </div>
        {qr && (
          <div className="mq-qr">
            <img src={qr} alt="Mã QR chuyển khoản" />
            Quét mã để chuyển khoản đúng số tiền và nội dung
          </div>
        )}
      </div>

      <h2>4. Điều khoản</h2>
      <div className="mq-terms"><ol>{terms.map((t, i) => <li key={i}>{t}</li>)}</ol></div>

      <div className="mq-sign">
        <div><div className="who">Đại diện bên mua</div><div className="how">(Ký, ghi rõ họ tên, đóng dấu / ký số)</div><div className="space" /><div>{c.representative}</div></div>
        <div><div className="who">Đại diện bên bán</div><div className="how">(Ký, ghi rõ họ tên, đóng dấu / ký số)</div><div className="space" /><div>{s.signerName}</div></div>
      </div>
      <div className="mq-foot">{[s.companyName, s.phone && `Hotline ${s.phone}`, s.email, s.website].filter(Boolean).join(' · ')}</div>
    </article>
  )
}
